import {TStructuredTalkingPoint} from '../interfaces/firestore/PredictionInterfaces';
import {TCompQuestion} from '../interfaces/firestore/FirestoreInterfaces';

export interface TalkingPointAllocation {
  questionIndex: number;
  talkingPoint: TStructuredTalkingPoint;
  articleId: string;
}

/**
 * Allocates talking points to question slots.
 *
 * Fixed-category talking points (win/score/ins_outs/hot/not_hot) are sorted to
 * the front in canonical order, then article-driven ones follow by rank.
 * Talking points marked insufficient_data are moved to the back so they only
 * get allocated if there are more questions than strong talking points.
 * No repeats. Questions beyond available talking points receive no allocation.
 */

const CATEGORY_ORDER: Record<string, number> = {
  win: 0,
  score: 1,
  ins_outs: 2,
  hot: 3,
  not_hot: 4,
  article_driven: 5,
};

export function allocateTalkingPoints(
  talkingPoints: TStructuredTalkingPoint[],
  questions: TCompQuestion[],
): TalkingPointAllocation[] {
  const sorted = [...talkingPoints].sort((a, b) => {
    const aInsufficient = a.insufficient_data ? 1 : 0;
    const bInsufficient = b.insufficient_data ? 1 : 0;
    if (aInsufficient !== bInsufficient) return aInsufficient - bInsufficient;
    const aOrder = CATEGORY_ORDER[a.category ?? 'article_driven'] ?? 5;
    const bOrder = CATEGORY_ORDER[b.category ?? 'article_driven'] ?? 5;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.rank - b.rank;
  });

  return sorted.slice(0, questions.length).map((tp, i) => ({
    questionIndex: i,
    talkingPoint: tp,
    articleId: tp.bestArticleId,
  }));
}
